import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Grape, Instagram, Facebook, Linkedin, Youtube, Send, ChevronRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useToast } from "../hooks/use-toast";
import { subscribeNewsletter } from "../services/api";

const Footer = () => {
  const { t } = useApp();
  const { toast } = useToast();
  const [email, setEmail] = useState("");

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await subscribeNewsletter(email); } catch { /* non-blocking */ }
    toast({ title: t("footer.subscribed") });
    setEmail("");
  };

  const shopLinks = [
    { to: "/agb", label: t("footer.agb") },
    { to: "/how-it-works", label: t("footer.process") },
    { to: "/label-designer", label: t("nav.label") },
    { to: "/store", label: t("footer.grapes") },
    { to: "/wine-configurator", label: t("nav.wine") },
    { to: "/grape-varieties", label: t("footer.grapesInfo") },
    { to: "/regions", label: t("nav.regions") },
    { to: "/producers", label: t("nav.producers") },
    { to: "/contacts", label: t("nav.contacts") },
    { to: "/datenschutz", label: t("footer.privacy") },
    { to: "/impressum", label: t("footer.impressum") },
    { to: "/account", label: t("auth.account") },
  ];

  return (
    <footer className="bg-wine-dark text-cream/80 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* brand + newsletter */}
          <div className="lg:col-span-5">
            <Link to="/" className="inline-block">
              <img src="/logo_moc.png" alt="My Own Cuvee" className="h-12 w-auto brightness-0 invert" />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-cream/60 max-w-sm">{t("footer.tagline")}</p>
            <h4 className="font-serif text-cream text-lg mt-8 mb-3">{t("footer.newsletter")}</h4>
            <p className="text-sm text-cream/60 mb-3">{t("footer.newsletterSub")}</p>
            <form onSubmit={subscribe} className="flex gap-2 max-w-sm">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@email.com" className="bg-cream/10 border-cream/20 text-cream placeholder:text-cream/40" />
              <Button type="submit" className="bg-gold hover:bg-gold-dark text-ink shrink-0"><Send className="w-4 h-4" /></Button>
            </form>
            <div className="flex gap-3 mt-6">
              {[
                [Instagram, "https://www.instagram.com/myowncuvee/", "Instagram"],
                [Facebook, "https://www.facebook.com/myowncuvee", "Facebook"],
                [Linkedin, "https://www.linkedin.com/company/6582007", "LinkedIn"],
                [Youtube, "https://www.youtube.com/user/isicel", "YouTube"],
              ].map(([Icon, href, label]) => (
                <a key={label} data-testid={`social-${label.toLowerCase()}`} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center hover:bg-gold hover:text-ink hover:border-gold transition-colors"><Icon className="w-4 h-4" /></a>
              ))}
            </div>
          </div>
          {/* shop links */}
          <div className="lg:col-span-7 lg:pl-8">
            <h4 className="font-serif text-cream text-xl mb-5 uppercase tracking-wide">{t("footer.shopTitle")}</h4>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
              {shopLinks.map((l, i) => (
                <li key={i} className="border-b border-cream/10">
                  <Link to={l.to} className="flex items-center gap-2 py-3 text-sm text-cream/80 hover:text-gold transition-colors group">
                    <ChevronRight className="w-4 h-4 text-gold/70 group-hover:translate-x-1 transition-transform" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-cream/10 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-cream/40">
          <span>© {new Date().getFullYear()} MYSYMP AG · MYOWNCUVEE. {t("footer.rights")}</span>
          <span>Studenstrasse 14B, CH-6207 Nottwil · info@myowncuvee.com</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
